/**
 * @file VerifyProfileForm.js
 * @description Component for verifying user profile during onboarding, email recovery, or self-registration
 */
import { useEffect, useState } from 'react';
import {
    getAnonymousUserOnboardingLookup,
    getUserAuthEmail,
    validateSelfRegistration,
} from '../../services/userService';
import Toast from '../ui/toasts/Toast';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { Dropdown } from '../helpers/Dropdown';
import PhoneInput from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { isValidPhoneNumber } from '../../utils/validate';
import { getUsStatesTerritories } from '../../utils/utils';
import { IdentifierEnum } from '../../utils/data/enums';
import LocationDropdown from '../shared/asyncDropdowns/LocationDropdown';
import DepartmentDropdown from '../shared/asyncDropdowns/DepartmentDropdown';
import DivisionDropdown from '../shared/asyncDropdowns/DivisionDropdown';
import RegionDropdown from '../shared/asyncDropdowns/RegionDropdown';
import BusinessUnitDropdown from '../shared/asyncDropdowns/BusinessUnitDropdown';
import PayGroupDropdown from '../shared/asyncDropdowns/PayGroupDropdown';
import CustomFieldInput from '../shared/CustomFieldInput';
import { getText } from '../../i18n';

const isCustomTextField = id =>
    id >= IdentifierEnum.CustomText1 && id <= IdentifierEnum.CustomText10;

const isCustomMoneyField = id =>
    id >= IdentifierEnum.CustomMoney1 && id <= IdentifierEnum.CustomMoney5;

const isCustomDateField = id =>
    id >= IdentifierEnum.CustomDate1 && id <= IdentifierEnum.CustomDate5;

const VerifyProfileForm = ({
    authData,
    isForgotEmail = false,
    isSelfRegistration = false,
    onVerificationComplete,
    handleOnboardingError,
}) => {
    const { companyCode, companyConfig, tenantId = null } = authData;
    const { application } = companyConfig;

    const [profile, setProfile] = useState({
        companyCode: companyCode || '',
        firstName: '',
        lastName: '',
        email: '',
        mobilePhoneNumber: '',
        uniqueId: '',
    });

    const [identifiers, setIdentifiers] = useState(
        application?.identifiers || [],
    );

    const [hasIdentifiers, setHasIdentifiers] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [usStatesTerritories, setUsStatesTerritories] = useState([]);

    useEffect(() => {
        if (identifiers?.length) {
            setHasIdentifiers(true);
        }
    }, [identifiers]);

    useEffect(() => {
        const states = [
            { label: getText('msg_1bcf1fee269d'), value: '' },
            ...getUsStatesTerritories().map(e => ({
                label: e.name,
                value: e.acronym,
            })),
        ];
        setUsStatesTerritories(states);
    }, []);

    const validateEmailAddress = email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoneNumber = phone => {
        if (!phone) {
            return false;
        }
        return isPossiblePhoneNumber(phone) && isValidPhoneNumber(phone);
    };

    const handleSubmit = async event => {
        event.preventDefault();
        const currentErrors = validateForm();

        if (Object.keys(currentErrors).length === 0) {
            try {
                setIsLoading(true);

                const identifiersData = identifiers.map(({ id }) => ({
                    id,
                    name: profile[id] != null ? String(profile[id]) : '',
                }));

                if (isSelfRegistration) {
                    const profileData = {
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                        email: profile.email || '',
                        mobilePhoneNumber: profile.mobilePhoneNumber || '',
                        uniqueId: profile.uniqueId || '',
                        identifiers: identifiersData,
                    };

                    await validateSelfRegistration(
                        profileData,
                        profile.companyCode || companyCode,
                    );

                    onVerificationComplete({
                        profileData,
                        isSelfRegistration: true,
                        companyCode: profile.companyCode,
                    });
                    return;
                }

                if (isForgotEmail) {
                    const response = await getUserAuthEmail(
                        identifiersData,
                        profile.companyCode,
                    );
                    const { email_address } = response;

                    if (!email_address) {
                        Toast.error(getText('msg_9366eac05efa'));
                        return;
                    }

                    onVerificationComplete(email_address);
                } else {
                    const response = await getAnonymousUserOnboardingLookup(
                        identifiersData,
                        profile.companyCode,
                        tenantId,
                    );

                    const { companyCode: code = null } = profile;
                    const { onboarding_link = null } = response;

                    if (!onboarding_link) {
                        Toast.error(
                            getText('msg_c2cf1650de4e'),
                        );
                        return;
                    }

                    const regex = /\/onboarding\/(\w+)\?/;
                    const match = onboarding_link.match(regex);
                    const onboardingCode = match ? match[1] : null;

                    if (!onboardingCode || !code) {
                        Toast.error(
                            getText('msg_cca6a146678e'),
                        );
                        return;
                    }
                    onVerificationComplete({
                        onboardingCode,
                        companyCode: code,
                    });
                }
            } catch (error) {
                console.error('Verification error', error);

                if (error?.response?.status === 404) {
                    Toast.error(
                        getText('msg_c09b1290aa69'),
                        {},
                        getText('msg_44deaa101284'),
                    );
                    return;
                }

                if (error?.response?.status === 400) {
                    const detail = error?.response?.data?.detail;
                    Toast.error(
                        detail ||
                            'An account matching your user record exists. Please return to the login screen to sign in or reset your credentials.',
                        {},
                        detail ? 'Bad Request' : 'Your account already exists.',
                    );
                    return;
                }

                if (error?.response?.data?.detail) {
                    Toast.error(error?.response?.data?.detail);
                } else {
                    Toast.error(
                        getText('msg_99614e90ddaa'),
                        {},
                        getText('msg_f6f8705261de'),
                    );
                }

                if (handleOnboardingError) {
                    handleOnboardingError(
                        error?.response?.data?.detail || 'Verification failed',
                    );
                }
            } finally {
                setIsLoading(false);
            }
        } else {
            setErrors(currentErrors);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (isSelfRegistration) {
            if (!profile.firstName?.trim()) {
                newErrors.firstName = 'First name is required';
            } else if (!/^[a-zA-Z\s'-]+$/.test(profile.firstName)) {
                newErrors.firstName = 'First name must contain only letters';
            }

            if (!profile.lastName?.trim()) {
                newErrors.lastName = 'Last name is required';
            } else if (!/^[a-zA-Z\s'-]+$/.test(profile.lastName)) {
                newErrors.lastName = 'Last name must contain only letters';
            }

            if (!profile.uniqueId?.trim()) {
                newErrors.uniqueId = 'Unique ID is required';
            }

            const hasEmail = profile.email?.trim();
            const hasPhone = profile.mobilePhoneNumber?.trim();

            if (!hasEmail && !hasPhone) {
                newErrors.email = 'Either email or phone number is required';
                newErrors.mobilePhoneNumber =
                    'Either email or phone number is required';
            }

            if (hasEmail && !validateEmailAddress(profile.email)) {
                newErrors.email = 'Invalid email address';
            }

            if (hasPhone && !validatePhoneNumber(profile.mobilePhoneNumber)) {
                newErrors.mobilePhoneNumber = 'Invalid phone number';
            }
        }

        identifiers.forEach(({ id, name }) => {
            if (isSelfRegistration && selfRegistrationCoreIds.includes(id)) {
                return;
            }

            const value = profile[id];
            const isEmpty = value == null || value === '' || value === 'null';
            if (isEmpty) {
                newErrors[id] = 'Required';
            } else if (
                name.toLowerCase().includes('id') &&
                !/^[a-zA-Z0-9]+$/.test(value)
            ) {
                newErrors[id] = 'Must be alphanumeric';
            }
        });

        if (!isSelfRegistration && !profile.companyCode?.trim()) {
            newErrors.companyCode = 'Required';
        }

        return newErrors;
    };

    const handleChange = (id, value) => {
        setProfile({ ...profile, [id]: value != null ? `${value}` : '' });

        if (errors[id]) {
            setErrors({ ...errors, [id]: null });
        }
    };

    const handleDateChange = (id, date) => {
        setProfile({ ...profile, [id]: date });
        if (errors[id]) {
            setErrors({ ...errors, [id]: null });
        }
    };

    const handlePhoneChange = value => {
        setProfile({ ...profile, mobilePhoneNumber: value });
        if (errors.mobilePhoneNumber) {
            setErrors({ ...errors, mobilePhoneNumber: null });
        }
    };

    const dateIdentifierIds = [
        IdentifierEnum.DOB,
        IdentifierEnum.Birthday,
        IdentifierEnum.HireDate,
        IdentifierEnum.CustomDate1,
        IdentifierEnum.CustomDate2,
        IdentifierEnum.CustomDate3,
        IdentifierEnum.CustomDate4,
        IdentifierEnum.CustomDate5,
    ];

    const isDateIdentifier = id => dateIdentifierIds.includes(id);

    const isWorkStateIdentifier = id =>
        isSelfRegistration && id === IdentifierEnum.WorkState;

    const renderSelfRegistrationField = identifier => {
        const { id, name } = identifier;

        if (id === IdentifierEnum.FirstName) {
            return (
                <div key={id} className="form-group mb-3">
                    <label
                        htmlFor="firstName"
                        className={errors.firstName ? 'text-danger' : ''}
                    >
                        {name || 'First Name'}
                        <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        placeholder={name || 'First Name'}
                        name="firstName"
                        id="firstName"
                        autoComplete="given-name"
                        onChange={e =>
                            handleChange('firstName', e.target.value)
                        }
                        value={profile.firstName || ''}
                        disabled={isLoading}
                    />
                    {errors.firstName && (
                        <div className="invalid-feedback">
                            {errors.firstName}
                        </div>
                    )}
                </div>
            );
        }

        if (id === IdentifierEnum.LastName) {
            return (
                <div key={id} className="form-group mb-3">
                    <label
                        htmlFor="lastName"
                        className={errors.lastName ? 'text-danger' : ''}
                    >
                        {name || 'Last Name'}
                        <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        placeholder={name || 'Last Name'}
                        name="lastName"
                        id="lastName"
                        autoComplete="family-name"
                        onChange={e => handleChange('lastName', e.target.value)}
                        value={profile.lastName || ''}
                        disabled={isLoading}
                    />
                    {errors.lastName && (
                        <div className="invalid-feedback">
                            {errors.lastName}
                        </div>
                    )}
                </div>
            );
        }

        if (id === IdentifierEnum.UniqueID) {
            return (
                <div key={id} className="form-group mb-3">
                    <label
                        htmlFor="uniqueId"
                        className={errors.uniqueId ? 'text-danger' : ''}
                    >
                        {name || 'Unique ID'}
                        <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${errors.uniqueId ? 'is-invalid' : ''}`}
                        placeholder={name || 'Unique ID'}
                        name="uniqueId"
                        id="uniqueId"
                        autoComplete="off"
                        onChange={e => handleChange('uniqueId', e.target.value)}
                        value={profile.uniqueId || ''}
                        disabled={isLoading}
                    />
                    {errors.uniqueId && (
                        <div className="invalid-feedback">
                            {errors.uniqueId}
                        </div>
                    )}
                </div>
            );
        }

        if (id === IdentifierEnum.EmailAddress) {
            return (
                <div key={id} className="form-group mb-3">
                    <label
                        htmlFor="email"
                        className={errors.email ? 'text-danger' : ''}
                    >
                        {name || 'Email'}
                    </label>
                    <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder={name || 'Email'}
                        name="email"
                        id="email"
                        autoComplete="email"
                        onChange={e => handleChange('email', e.target.value)}
                        value={profile.email || ''}
                        disabled={isLoading}
                    />
                    {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                    )}
                    {!errors.email && (
                        <small className="form-text text-muted">
                            {getText('msg_cd900ef85e65')}
                        </small>
                    )}
                </div>
            );
        }

        if (id === IdentifierEnum.MobilePhoneNumber) {
            return (
                <div key={id} className="form-group mb-3">
                    <label
                        htmlFor="mobilePhoneNumber"
                        className={
                            errors.mobilePhoneNumber ? 'text-danger' : ''
                        }
                    >
                        {name || 'Phone'}
                    </label>
                    <PhoneInput
                        country="US"
                        className={`form-control ${errors.mobilePhoneNumber ? 'is-invalid' : ''}`}
                        onChange={handlePhoneChange}
                        name="mobilePhoneNumber"
                        id="mobilePhoneNumber"
                        placeholder={name || 'Phone'}
                        value={profile.mobilePhoneNumber || ''}
                        disabled={isLoading}
                    />
                    {errors.mobilePhoneNumber && (
                        <div className="invalid-feedback">
                            {errors.mobilePhoneNumber}
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };

    const selfRegistrationCoreIds = [
        IdentifierEnum.FirstName,
        IdentifierEnum.LastName,
        IdentifierEnum.UniqueID,
        IdentifierEnum.EmailAddress,
        IdentifierEnum.MobilePhoneNumber,
    ];

    const renderFieldLabel = (id, name) => (
        <label
            htmlFor={`identifier-${id}`}
            className={errors[id] ? 'text-danger' : ''}
        >
            {name}
            <span className="text-danger">*</span>
        </label>
    );

    const renderAsyncDropdownPlaceholder = (id, name) => (
        <div key={id} className="form-group mb-3">
            {renderFieldLabel(id, name)}
            <select className="form-control" disabled>
                <option>{getText('msg_b04ba49f8486')}</option>
            </select>
        </div>
    );

    const renderField = identifier => {
        const { id, name } = identifier;

        if (isDateIdentifier(id)) {
            return (
                <div key={id} className="form-group mb-3 d-flex flex-column">
                    {renderFieldLabel(id, name)}
                    <CustomDatePicker
                        selectedDate={profile[id]}
                        onDateChange={date => handleDateChange(id, date)}
                        errorMessage={errors[id]}
                        disabled={isLoading}
                        required
                    />
                </div>
            );
        }

        if (isWorkStateIdentifier(id)) {
            return (
                <div key={id} className="form-group mb-3">
                    {renderFieldLabel(id, name)}
                    <Dropdown
                        name={`identifier-${id}`}
                        defaultValue={
                            usStatesTerritories.find(
                                s => s.value === profile[id],
                            )?.label || 'Select State'
                        }
                        options={usStatesTerritories}
                        onChange={value => handleChange(id, value)}
                        value={profile[id] || ''}
                        className={errors[id] ? 'is-invalid' : ''}
                        disabled={isLoading}
                    />
                    {errors[id] && (
                        <div className="invalid-feedback">{errors[id]}</div>
                    )}
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.Department) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <DepartmentDropdown
                        inlineLabel={false}
                        selectedDepartment={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onDepartmentChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.Division) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <DivisionDropdown
                        inlineLabel={false}
                        selectedDivision={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onDivisionChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.Location) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <LocationDropdown
                        inlineLabel={false}
                        selectedLocation={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onLocationChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.Region) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <RegionDropdown
                        inlineLabel={false}
                        selectedRegion={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onRegionChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.BusinessUnit) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <BusinessUnitDropdown
                        inlineLabel={false}
                        selectedBusinessUnit={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onBusinessUnitChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (isSelfRegistration && id === IdentifierEnum.PayGroup) {
            if (!profile.companyCode && !tenantId)
                return renderAsyncDropdownPlaceholder(id, name);
            return (
                <div key={id} className="form-group mb-3">
                    <PayGroupDropdown
                        inlineLabel={false}
                        selectedPayGroup={profile[id]}
                        tenantId={tenantId}
                        companyCode={profile.companyCode || companyCode}
                        isPublic={true}
                        onPayGroupChange={value => handleChange(id, value)}
                        errorMessage={errors[id]}
                        required
                    />
                </div>
            );
        }

        if (
            isCustomTextField(id) ||
            isCustomMoneyField(id) ||
            isCustomDateField(id)
        ) {
            return (
                <div key={id} className="form-group mb-3">
                    <CustomFieldInput
                        field={{ fieldName: id, value: profile[id] }}
                        fieldName={name}
                        isDateField={isCustomDateField(id)}
                        isMoneyField={isCustomMoneyField(id)}
                        isTextField={isCustomTextField(id)}
                        inlineLabel={false}
                        required
                        errorMessage={errors[id]}
                        handleInputChange={(_, value) =>
                            isCustomDateField(id)
                                ? handleDateChange(id, value)
                                : handleChange(id, value)
                        }
                    />
                </div>
            );
        }

        return (
            <div key={id} className="form-group mb-3">
                {renderFieldLabel(id, name)}
                <input
                    type="text"
                    className={`form-control ${errors[id] ? 'is-invalid' : ''}`}
                    placeholder={name}
                    name={`identifier-${id}`}
                    id={`identifier-${id}`}
                    autoComplete="on"
                    onChange={e => handleChange(id, e.target.value)}
                    value={profile[id] || ''}
                    disabled={isLoading}
                />
                {errors[id] && (
                    <div className="invalid-feedback">{errors[id]}</div>
                )}
            </div>
        );
    };

    const getHeadingText = () => {
        if (isSelfRegistration) {
            return 'Create Account';
        }
        if (isForgotEmail) {
            return 'Verify Identity';
        }
        return 'Verify Identity';
    };

    const getSubheadingText = () => {
        if (isSelfRegistration) {
            return '';
        }
        if (isForgotEmail) {
            return 'Please provide the following information to recover your username.';
        }
        return '';
    };

    return (
        <div className="form-container p-3">
            {!hasIdentifiers && !isSelfRegistration ? (
                <>
                    <h4 className="text-center">
                        {getText('msg_5efa7b5c93ed')}
                    </h4>
                    <p className="text-center mb-4">
                        {getText('msg_2fff45d0a058')}
                    </p>
                </>
            ) : (
                <>
                    <h2 className="iNGAGED-Typography-Heading3 mb-3">
                        {getHeadingText()}
                    </h2>

                    {getSubheadingText() && (
                        <small className="text-muted">
                            {getSubheadingText()}
                        </small>
                    )}

                    <form
                        className="needs-validation text-left py-3"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        {isSelfRegistration
                            ? identifiers.map(identifier => {
                                  if (
                                      selfRegistrationCoreIds.includes(
                                          identifier.id,
                                      )
                                  ) {
                                      return renderSelfRegistrationField(
                                          identifier,
                                      );
                                  }
                                  return renderField(identifier);
                              })
                            : identifiers.map(renderField)}

                        {!companyCode &&
                            !isSelfRegistration &&
                            renderField({
                                id: 'companyCode',
                                name: 'Company Code',
                            })}

                        <div className="form-group d-grid gap-2 col-4 mx-auto text-center pt-4 pb-2">
                            <button
                                type="submit"
                                className="ing-button-primary-left"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    'Loading...'
                                ) : (
                                    <>
                                        {getText('msg_bc981983e7f5')}
                                        <i className="bi bi-chevron-right"></i>
                                    </>
                                )}
                            </button>
                        </div>

                        {isSelfRegistration && (
                            <div className="form-group text-center pt-2">
                                <small className="text-muted">
                                    <span className="text-danger">*</span>{' '}
                                    {getText('msg_7c118f042eaa')}
                                </small>
                            </div>
                        )}
                    </form>
                </>
            )}
            <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                <p>
                    <i className="bi bi-envelope" />
                    {' '}
                    {getText('msg_97e2e9fd5798')}
                    {' '}
                    <a
                        href={`/get-support${
                            companyCode ? `?companyCode=${companyCode}` : ''
                        }`}
                        className="pl-2"
                    >
                        {getText('msg_fe1a2922069b')}
                    </a>
                </p>
            </div>
        </div>
    );
};

export default VerifyProfileForm;
